/**
 * Длина записи по звуку — на стороне ученика (12.09.2026).
 *
 * 🔴 ЗАЧЕМ ДЕКОД, А НЕ ЗАГОЛОВОК. Метки времени в заголовке врут: на проде
 * 2,4 секунды звука лежали в базе как 301,7 (сверка `CHECK-2026-09-11` §1.6), а
 * у записи с микрофона длины в заголовке часто нет вовсе — браузер отдаёт
 * бесконечность. Считаем так же, как сервер: разворачиваем в отсчёты.
 *
 * ⚠️ ЗАМЕР НИКОГДА НЕ БРОСАЕТ И НЕ ВИСНЕТ. Он стоит между «Остановить» и
 * «Отправить»: исключение отняло бы у ученика готовую запись, а зависание
 * навсегда заперло бы кнопку отправки. Не смогли — `null`, решает
 * `recordingIntegrity`.
 *
 * ⚠️ ДЕКОДИРУЕМ НА 8 кГц. Контекст сам пересчитывает частоту, а памяти уходит в
 * шесть раз меньше: три минуты на 48 кГц — это 34 МБ отсчётов, и на телефоне
 * такой замер убивает вкладку вместе с записью.
 */

/** Больше этого не декодируем: столько сжатого звука развернётся в слишком много отсчётов. */
export const MAX_DECODE_BYTES = 25 * 1024 * 1024;

/** Сколько ждать декода, прежде чем признать замер несостоявшимся. */
export const DECODE_TIMEOUT_MS = 10_000;

type DecodeContext = {
  decodeAudioData(buf: ArrayBuffer): Promise<{ duration: number }>;
  close?(): void | Promise<void>;
};

export type DecodeDeps = {
  /** Подменяется в проверках; в браузере — WebAudio. */
  makeContext?: () => DecodeContext | null;
  timeoutMs?: number;
};

function browserContext(): DecodeContext | null {
  const w = globalThis as unknown as {
    OfflineAudioContext?: new (ch: number, len: number, rate: number) => DecodeContext;
    webkitOfflineAudioContext?: new (ch: number, len: number, rate: number) => DecodeContext;
    AudioContext?: new () => DecodeContext;
    webkitAudioContext?: new () => DecodeContext;
  };
  const Offline = w.OfflineAudioContext ?? w.webkitOfflineAudioContext;
  if (Offline) {
    try {
      return new Offline(1, 1, 8000);
    } catch {
      /* частота не подошла — пробуем обычный контекст */
    }
  }
  const Live = w.AudioContext ?? w.webkitAudioContext;
  return Live ? new Live() : null;
}

/**
 * Есть ли чем мерить. По этому признаку блок решает, предупреждать ли ученика
 * «длину измерить не удалось» или промолчать: «пытались и не смогли» и «нечем» —
 * разные вещи, и вторая пугать не должна.
 */
export function canMeasureAudio(deps: DecodeDeps = {}): boolean {
  const make = deps.makeContext ?? browserContext;
  try {
    const ctx = make();
    if (!ctx) return false;
    void closeQuietly(ctx);
    return true;
  } catch {
    return false;
  }
}

function closeQuietly(ctx: DecodeContext | null): void {
  try {
    void ctx?.close?.();
  } catch {
    /* закрывать нечего */
  }
}

export async function measureBlobSeconds(blob: Blob, deps: DecodeDeps = {}): Promise<number | null> {
  if (!blob || !blob.size || blob.size > MAX_DECODE_BYTES) return null;
  const make = deps.makeContext ?? browserContext;
  const timeoutMs = deps.timeoutMs ?? DECODE_TIMEOUT_MS;

  let ctx: DecodeContext | null = null;
  try {
    ctx = make();
    if (!ctx) return null;
    const buf = await blob.arrayBuffer();
    const decoded = await Promise.race([
      ctx.decodeAudioData(buf),
      new Promise<null>((resolve) => setTimeout(() => resolve(null), timeoutMs)),
    ]);
    const seconds = decoded?.duration;
    return typeof seconds === 'number' && Number.isFinite(seconds) ? seconds : null;
  } catch {
    return null;
  } finally {
    closeQuietly(ctx);
  }
}
